import { DateFormatter } from '@components'

import {
  amountToDisplayValue,
  Attachment,
  Badge,
  Color,
  Flex,
  getStringFromReactNode,
  Spacing,
  Text,
} from '@billy/design-system'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'
import { getAttachmentEInvoiceTypeBadge } from '@features/eInvoicing/utils/getAttachmentEInvoiceTypeBadge'

import { getLocalDateFromDateString } from '../../../../../../../../utils/getLocalDateFromDateString'

const DEFAULT_TITLE_VALUE = '-'

interface AttachmentTileContentProps {
  attachment: Attachment
  isShoeboxReadOnly?: boolean
}

export const AttachmentTileContent = ({
  attachment,
  isShoeboxReadOnly = false,
}: AttachmentTileContentProps): ReactElement => {
  const { amount, documentDate, file, supplier } = attachment
  const { t } = useTranslation()
  const isEInvoicingOnly = useIsEInvoicingOnly()

  const title = supplier || file.fileName
  const attachmentTileAmount = useMemo(() => (amount ? amountToDisplayValue(amount) : '-'), [amount])
  const attachmentTileTitle = useMemo(() => getStringFromReactNode(title) || DEFAULT_TITLE_VALUE, [title])

  const attachmentTileDate = useMemo(() => getLocalDateFromDateString(documentDate || ''), [documentDate])

  const attachmentTypeBadge = useMemo(() => {
    const badgeConfig = getAttachmentEInvoiceTypeBadge({ attachment, isEInvoicingOnly, isShoeboxReadOnly })

    if (!badgeConfig) {
      return null
    }

    return (
      <Flex>
        <Badge color={badgeConfig.color} mode="subtle">
          {t(badgeConfig.labelKey)}
        </Badge>
      </Flex>
    )
  }, [attachment, isEInvoicingOnly, isShoeboxReadOnly, t])

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between" gap={Spacing.S8}>
        <Text as="h3" color={Color.Grey950} variant="bodyLG" weight="medium" truncate>
          {attachmentTileTitle}
        </Text>
        {attachmentTypeBadge}
      </Flex>
      <Flex alignItems="center" justifyContent="space-between">
        <Text colorVariant="secondary">{attachmentTileDate ? <DateFormatter value={attachmentTileDate} /> : '-'}</Text>
        <Text color={Color.Grey950} variant="bodyLG" weight="medium">
          {attachmentTileAmount}
        </Text>
      </Flex>
    </>
  )
}
